import { OrgUser } from '@/models/Users'

const serverURL = import.meta.env.VITE_API_URL

// ORG USERS //

// get all users in an organisation (org admin only)
export async function getOrgUsers(
  orgUuid: string,
  token: string,
): Promise<OrgUser[]> {
  const response = await fetch(`${serverURL}/orgs/${orgUuid}/users`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch organisation users')
  }

  return response.json()
}

interface UpdateDeleteOrgUserFn {
  userUuid: string
  orgUuid: string
  token: string
}

// remove a user from an organisation (org admin only)
export async function deleteOrgUser({
  userUuid,
  orgUuid,
  token,
}: UpdateDeleteOrgUserFn): Promise<void> {
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/users/${userUuid}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error(
        'This is the only admin in the organisation — promote another user to admin before removing them.',
      )
    }
    throw new Error('Failed to remove user from organisation')
  }
}

// update a user's role in an organisation (org admin only)
interface UpdateOrgUserRoleFn extends UpdateDeleteOrgUserFn {
  role: 'admin' | 'user'
}

export async function updateOrgUser({
  userUuid,
  orgUuid,
  role,
  token,
}: UpdateOrgUserRoleFn): Promise<void> {
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/role/${userUuid}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    },
  )

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error(
        'This is the only admin in the organisation — promote another user to admin first.',
      )
    }
    throw new Error('Failed to update user in organisation')
  }
}

// INVITATIONS //

interface SendOrgUserInvitationFn {
  email: string | null
  orgUuid: string
  inviterUuid: string
  role: 'admin' | 'user'
  token: string
}

// invite a user to an organisation (org admin only)

export async function sendOrgUserInvitation({
  email,
  inviterUuid,
  orgUuid,
  role,
  token,
}: SendOrgUserInvitationFn) {
  const response = await fetch(`${serverURL}/invitations/${orgUuid}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invitee_email: email,
      inviter_uuid: inviterUuid,
      role,
    }),
  })

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error('User already invited')
    } else if (response.status === 400) {
      throw new Error('User is already part of this organisation')
    } else throw new Error('Failed to send invitation')
  }

  return response.json()
}

// get all invitations for an organisation (org admin only)
export async function getInvitations(token: string, orgUuid: string) {
  const response = await fetch(`${serverURL}/invitations/org/${orgUuid}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch invitations')
  }

  return response.json()
}

interface DeleteInvitationFn {
  token: string
  orgUuid: string
  invitationUuid: string
}

// delete an invitation (org admin only)

export async function deleteInvitation({
  token,
  orgUuid,
  invitationUuid,
}: DeleteInvitationFn) {
  const response = await fetch(
    `${serverURL}/invitations/${orgUuid}/${invitationUuid}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    const error = await response.json()
    if (response.status === 404) {
      throw new Error('Invitation not found')
    } else {
      throw new Error('Failed to delete invitation')
    }
  }
  return response
}

interface UpdateInvitationStatusFn {
  invitationUuid: string
  status: 'accepted' | 'declined'
  token: string
}

// update the status of an invitation to accepted or declined

export async function updateInvitationStatus({
  token,
  invitationUuid,
  status,
}: UpdateInvitationStatusFn) {
  const response = await fetch(`${serverURL}/invitations/${invitationUuid}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ invitation_status: status }),
  })

  if (!response.ok) {
    const error = await response.json()
    if (response.status === 403 && error.detail?.includes('email')) {
      throw new Error(
        'Failed to update invitation, user id does not match email.',
      )
    } else if (
      response.status === 403 &&
      error.detail?.includes('not verified')
    ) {
      // Only a genuine unverified-account 403 drives the verify flow. Other
      // 403s (e.g. an Auth0 Management token that has lost scope) must NOT be
      // mistaken for "needs verification" — they fall through to the generic
      // error so the user isn't stranded on an unresolvable verify step.
      throw new Error('Failed to update invitation. Verification required.')
    } else if (response.status === 404) {
      throw new Error('Invitation not found')
    } else if (response.status === 400 && error.detail.includes('expired')) {
      throw new Error('Invitation has expired')
    } else if (response.status === 400) {
      throw new Error('User is already part of this organisation')
    }
    throw new Error(`Failed to update invitation`)
  }

  return response
}

// Resend the Auth0 verification email to the currently authenticated user.
// Used on the invitation verify step: fired once automatically when the user
// first lands there, and again on demand via the "Resend email" button.
export async function resendInvitationVerification(
  token: string,
): Promise<{ sent: boolean }> {
  const response = await fetch(
    `${serverURL}/invitations/resend-verification`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  )

  if (!response.ok) {
    throw new Error('Failed to resend verification email')
  }

  return response.json()
}

export interface PendingInvitation {
  invitation_uuid: string
  org_uuid: string
  org_name: string
  created_at: string
}

export async function getMyPendingInvitations(
  token: string,
): Promise<PendingInvitation[]> {
  const response = await fetch(`${serverURL}/invitations/my-invitations`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch pending invitations')
  }

  return response.json()
}

export async function getInvitationDetails(
  invitationUuid: string,
  token: string,
) {
  const response = await fetch(`${serverURL}/invitations/${invitationUuid}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Invitation not found')
    } else {
      throw new Error('Failed to fetch invitation details')
    }
  }

  return response.json()
}
